import { FC, useEffect, useState } from "react";
import { Box, Button, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus } from "../../utils/api.types";
import { updateBatch } from "../../store/batchStore/batchThunk";

const toDateInput = (d: any) => (d ? new Date(d).toISOString().slice(0, 10) : "");

interface BatchFormProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
  batch?: any; // the selected batch row (edit only)
}

// Right-side drawer form (same shell as the Location / Record Expense Payment
// drawer): sticky DialogTitle header, scrollable DialogContent, fixed
// DialogActions footer. Batches are edit-only (created via Goods Receipt).
const BatchForm: FC<BatchFormProps> = ({ open, onClose, batch }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.batches);

  const [mrp, setMrp] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [mfgDate, setMfgDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  // hydrate each time the drawer opens
  useEffect(() => {
    if (!open || !batch) return;
    setMrp(batch.mrp ?? "");
    setSalePrice(batch.salePrice ?? "");
    setPurchasePrice(batch.purchasePrice ?? "");
    setMfgDate(toDateInput(batch.mfgDate));
    setExpiryDate(toDateInput(batch.expiryDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, batch?._id]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batch?._id) return;
    const payload = {
      mrp: Number(mrp) || 0,
      salePrice: Number(salePrice) || 0,
      mfgDate: mfgDate || null,
      // purchasePrice and expiryDate are part of the batch's identity/valuation
      // basis (set at goods receipt) and are locked — see the disabled fields below.
    };
    try {
      await dispatch(updateBatch({ id: batch._id, payload })).unwrap();
      onClose(true);
    } catch (err: any) {
      toast.error(err?.message || "Failed to update batch");
    }
  };

  const itemName = batch?.skuId?.name || "";
  const baseUnit = batch?.skuId?.baseUnitName || "unit";
  // base units per receiving unit (captured at GRN); recover from the cost pair
  // for legacy batches, else 1:1 — same rule the backend uses on save.
  const baseFactor =
    batch?.baseFactor > 0
      ? batch.baseFactor
      : batch?.purchasePrice > 0 && batch?.unitPurchaseCost > 0
      ? batch.purchasePrice / batch.unitPurchaseCost
      : 1;
  const round4 = (n: number) => Math.round((Number(n) || 0) * 10000) / 10000;
  // Live per-base-unit rates as the user types (mirror <field> / baseFactor).
  const liveUnitSale = round4((Number(salePrice) || 0) / baseFactor);
  const liveUnitMrp = round4((Number(mrp) || 0) / baseFactor);

  return (
    <div className="drawer-form-container" style={{ width: "100%", maxWidth: "560px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: "2px 0 0 2px",
          position: "relative",
        }}
        component="form"
        onSubmit={onSubmit}
        noValidate
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", minHeight: "40px" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1.125rem" }}>
              Edit Batch
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ padding: "0 24px 100px", overflowY: "auto", flex: 1 }}>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Item"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={itemName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Batch #"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{ readOnly: true }}
                value={batch?.batchNumber || ""}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="MRP (₹)"
                type="number"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={mrp}
                onChange={(e) => setMrp(e.target.value)}
                helperText={`≈ ₹${liveUnitMrp} / ${baseUnit} (per base unit)`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Sale Price (₹)"
                type="number"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                helperText={`≈ ₹${liveUnitSale} / ${baseUnit} (per base unit — invoice rate)`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Purchase Price (₹)"
                type="number"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={purchasePrice}
                disabled
                helperText={`≈ ₹${round4(batch?.unitPurchaseCost)} / ${baseUnit} · set at goods receipt — reverse/re-post the GRN to change it`}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Mfg date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={mfgDate}
                onChange={(e) => setMfgDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expiry date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={expiryDate}
                disabled
                helperText="Part of the batch's identity — reverse/re-post the GRN to correct it"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid #c1c5cb",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            padding: "16px 24px",
            margin: 0,
            zIndex: 1000,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, width: "100%" }}>
            <Button variant="outlined" onClick={() => onClose(false)} sx={{ minWidth: "100px", height: "40px" }}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={status === ApiStatus.LOADING}
              sx={{ minWidth: "140px", height: "40px" }}
            >
              Update
            </LoadingButton>
          </Box>
        </DialogActions>
      </Box>
    </div>
  );
};

export default BatchForm;
